import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from '@/components/ui/Container';
import SidePanel from '@/components/ui/SidePanel';
import { useProjectDetailShell } from '@hooks/projects/detail/useProjectDetail';
import { getTabComponent } from './project';
import {
    TabContent,
    ContentCard,
    RolesSectionTitle,
    MetaValueInherit,
} from './style';
import KpiCard from '@/components/ui/analytics/KPICard';
import { AimOutlined } from '@ant-design/icons';

/**
 * Project detail shell: UI only. All logic lives in useProjectDetailShell.
 * Uses Container and global styles from style.js.
 */
const ProjectDetail = () => {
    const navigate = useNavigate();
    const {
        projectId,
        project,
        projectError,
        effectiveTabId,
        goToChat,
        backToProjectsPath,
        showChatButton,
    } = useProjectDetailShell();

    const projectShellReady =
        !!project &&
        !projectError &&
        String(project?.id ?? project?._id ?? '') === String(projectId ?? '');

    const isDiscussionTab = useMemo(() => {
        const id = String(effectiveTabId ?? '').trim().toLowerCase();
        return id === 'discussion' || id === 'discussions';
    }, [effectiveTabId]);

    /** Keep the last "main" tab so Discussion can open in SidePanel without replacing body content. */
    const [lastMainTabId, setLastMainTabId] = useState('details');
    useEffect(() => {
        if (!effectiveTabId) return;
        if (isDiscussionTab) return;
        setLastMainTabId(effectiveTabId);
    }, [effectiveTabId, isDiscussionTab]);

    const mainTabId = isDiscussionTab ? (lastMainTabId || 'details') : effectiveTabId;

    const isSprintsTab = mainTabId === 'sprints' || mainTabId === 'tasks' || mainTabId === 'task';
    const isQATab = mainTabId === 'qa';
    const isBlockersTab = mainTabId === 'blockers' || mainTabId === 'B';
    const isPMOTab =
        mainTabId === 'completion-requests' ||
        mainTabId === 'completionRequests' ||
        mainTabId === 'pmo' ||
        mainTabId === 'project-pmo';
    const isIssuesAndUpgradesTab =
        mainTabId === 'issues-and-upgrades' ||
        mainTabId === 'issuesAndUpgrades';
    const isClientTicketsTab =
        mainTabId === 'client-tickets' ||
        mainTabId === 'clientTickets';
    /** Kanban-style tabs: natural height + white surface. Discussion is full-height Chat-like, not kanban. */
    const isKanbanTab = isBlockersTab || isIssuesAndUpgradesTab || isClientTicketsTab;
    const [createSprintModalOpen, setCreateSprintModalOpen] = useState(false);
    const ActiveTab = getTabComponent(mainTabId);
    const DiscussionTab = getTabComponent('discussion');

    if (!projectId) {
        navigate(backToProjectsPath);
        return null;
    }

    /** Initial / mismatched fetch: `InsideBody` already shows the shell loader — avoid a second Spin here. */
    if (!projectShellReady && !projectError) {
        return null;
    }

    if (projectError || !project) {
        const errorMsg = projectError?.response?.data?.message || projectError?.message || (projectError ? String(projectError) : null);
        const errorStatus = projectError?.response?.status;
        const errorDetail = projectError?.response?.data?.error || projectError?.response?.data;

        return (
            <Container $display="flex" $flexDirection="column" $gap="24px" $width="100%">
                <RolesSectionTitle>Project Details</RolesSectionTitle>
                <ContentCard $padding="24px">
                    <MetaValueInherit style={{ color: '#ff4d4f', fontWeight: '600', fontSize: '16px', marginBottom: '8px' }}>
                        Failed to load project details.
                    </MetaValueInherit>
                    {errorMsg && (
                        <div style={{ 
                            marginTop: '12px', 
                            padding: '16px', 
                            borderRadius: '8px', 
                            background: '#fff2f0', 
                            border: '1px solid #ffccc7',
                            color: '#ff4d4f',
                            fontFamily: 'var(--font-family-geist), monospace',
                            fontSize: '14px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all'
                        }}>
                            <div style={{ fontWeight: '600', marginBottom: '4px' }}>Error Message:</div>
                            <div style={{ marginBottom: '12px', color: '#a8071a' }}>{errorMsg}</div>
                            
                            {errorStatus && (
                                <div style={{ marginBottom: '12px' }}>
                                    <span style={{ fontWeight: '600' }}>Status Code:</span> <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '4px', border: '1px solid #ffa39e' }}>{errorStatus}</code>
                                </div>
                            )}
                            
                            {errorDetail && (
                                <div style={{ marginTop: '12px', borderTop: '1px dashed #ffccc7', paddingTop: '12px' }}>
                                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>Technical Details:</div>
                                    <pre style={{ 
                                        margin: '0', 
                                        padding: '8px',
                                        background: '#fff',
                                        border: '1px solid #ffe8e6',
                                        borderRadius: '4px',
                                        whiteSpace: 'pre-wrap', 
                                        fontSize: '12px', 
                                        color: '#434343',
                                        maxHeight: '200px',
                                        overflowY: 'auto'
                                    }}>
                                        {typeof errorDetail === 'object' ? JSON.stringify(errorDetail, null, 2) : String(errorDetail)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    )}
                    {!projectError && !project && (
                        <div style={{ 
                            marginTop: '12px', 
                            padding: '12px', 
                            borderRadius: '8px', 
                            background: '#fffbe6', 
                            border: '1px solid #ffe58f',
                            color: '#d46b08',
                            fontSize: '14px'
                        }}>
                            Project details are empty or not initialized. No active query error is registered.
                        </div>
                    )}
                </ContentCard>
            </Container>
        );
    }

    return (
        <>
            <Container
                $display="flex"
                $flexDirection="column"
                $gap="0"
                $width="100%"
                $flex="1"
                $minHeight="0"
                $overflow="hidden"
            >


                <TabContent $sprintsWhiteSurface={isKanbanTab ? true : undefined}>
                    <ActiveTab
                        projectId={projectId}
                        project={project}
                        showChatButton={
                            isSprintsTab || isQATab || isBlockersTab || isPMOTab || isIssuesAndUpgradesTab || isClientTicketsTab
                                ? showChatButton
                                : undefined
                        }
                        onGoToChat={
                            isSprintsTab || isQATab || isBlockersTab || isPMOTab || isIssuesAndUpgradesTab || isClientTicketsTab
                                ? goToChat
                                : undefined
                        }
                        createSprintModalOpen={isSprintsTab ? createSprintModalOpen : undefined}
                        setCreateSprintModalOpen={isSprintsTab ? setCreateSprintModalOpen : undefined}
                    />
                </TabContent>
            </Container>

            <SidePanel
                open={isDiscussionTab}
                title="Discussion"
                width="45%"
                bodyPadding="0"
                bodyOverflowY="hidden"
                closeOnOverlayClick={false}
                onClose={() => {
                    const nextTab = lastMainTabId || 'details';
                    navigate({ search: `?tab=${encodeURIComponent(nextTab)}` }, { replace: false });
                }}
            >
                {isDiscussionTab && <DiscussionTab projectId={projectId} project={project} />}
            </SidePanel>
        </>
    );
};

export default ProjectDetail;
